import { ErrorMessage, Field, useField } from 'formik'
import merge from 'lodash/merge'
import * as Yup from 'yup'
import { FieldOrValueSelector } from '~/custom_reports/builder/calculated_fields/FieldOrValueSelector'
import { fieldPathKeys } from '~/custom_reports/shared'
import { Checkbox, FormControl, FormErrorMessage, FormLabel, HStack, Input } from '~/design_system'
import { Case, getCaseFieldValidations } from './CaseFormula'
import { DateTrunc, DateTruncValidations } from './DateTrunc'
import { FieldSelector } from './FieldSelector'
import { PadText, PadTextValidations } from './PadText'

const Add = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['integer', 'number']} maxFields={5} />
)

const DateDiff = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['datetime', 'date']} maxFields={2} />
)

const Age = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['datetime', 'date']} maxFields={1} />
)

const Min = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['datetime', 'date', 'integer', 'number']} maxFields={1} />
)

const Max = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['datetime', 'date', 'integer', 'number']} maxFields={1} />
)

const Concat = ({ calculatedField }) => {
  const [, { touched, error }] = useField('separator')

  return (
    <>
      <FieldOrValueSelector
        calculatedField={calculatedField}
        types={['number', 'integer', 'datetime', 'date', 'string']}
        maxFields={7}
      />
      <FormControl id="separator" isInvalid={touched && error}>
        <FormLabel>Separator</FormLabel>
        <Field name="separator" maxW="3xs" as={Input} />
        <FormErrorMessage>
          <ErrorMessage name="separator" />
        </FormErrorMessage>
      </FormControl>
    </>
  )
}

const ArrayToString = ({ calculatedField }) => (
  <FieldSelector types={['array']} calculatedField={calculatedField} maxFields={1} />
)

const Coalesce = ({ calculatedField }) => <FieldSelector calculatedField={calculatedField} maxFields={8} />

const Sum = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['number', 'integer']} maxFields={8} />
)

const Divide = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['number', 'integer']} maxFields={2} />
)

const Subtract = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['number', 'integer']} maxFields={2} />
)

const Multiply = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['number', 'integer']} maxFields={2} />
)

const Count = ({ calculatedField }) => {
  const [, { touched, error }] = useField('distinct')

  return (
    <>
      <FieldSelector
        calculatedField={calculatedField}
        maxFields={1}
        types={['number', 'integer', 'datetime', 'date', 'string']}
      />
      <FormControl isInvalid={touched && error}>
        <Field type="checkbox" id="distinct" name="distinct" as={Checkbox}>
          Distinct?
        </Field>
        <FormErrorMessage>
          <ErrorMessage name="distinct" />
        </FormErrorMessage>
      </FormControl>
    </>
  )
}

const LeftOrRight = ({ calculatedField }) => {
  const [, { touched, error }] = useField('length')

  return (
    <>
      <FieldSelector calculatedField={calculatedField} types={['number', 'integer', 'string']} maxFields={1} />
      <FormControl id="length" isRequired isInvalid={touched && error}>
        <FormLabel>Length</FormLabel>
        <Field type="number" name="length" maxW="3xs" as={Input} />
        <FormErrorMessage>
          <ErrorMessage name="length" />
        </FormErrorMessage>
      </FormControl>
    </>
  )
}

export const Substring = ({ calculatedField }) => {
  const [, { touched: startIndexTouched, error: startIndexError }] = useField('start_index')
  const [, { touched: lengthTouched, error: lengthError }] = useField('length')

  return (
    <>
      <FieldSelector calculatedField={calculatedField} types={['number', 'integer', 'string']} maxFields={1} />
      <HStack align="flex-start">
        <FormControl id="start_index" isRequired isInvalid={startIndexTouched && startIndexError} w="inherit">
          <FormLabel>Start Index</FormLabel>
          <Field type="number" name="start_index" maxW="3xs" as={Input} />
          <FormErrorMessage>
            <ErrorMessage name="start_index" />
          </FormErrorMessage>
        </FormControl>
        <FormControl id="length" isInvalid={lengthTouched && lengthError} w="inherit">
          <FormLabel>Length (leave blank for last index)</FormLabel>
          <Field type="number" name="length" as={Input} maxW="3xs" />
          <FormErrorMessage>
            <ErrorMessage name="length" />
          </FormErrorMessage>
        </FormControl>
      </HStack>
    </>
  )
}

const Value = () => {
  const [, { touched, error }] = useField('value')

  return (
    <FormControl id="value" isRequired isInvalid={touched && error}>
      <FormLabel>Value</FormLabel>
      <Field name="value" placeholder="Enter value" as={Input} maxW="3xs" />
      <FormErrorMessage>
        <ErrorMessage name="value" />
      </FormErrorMessage>
    </FormControl>
  )
}

const ToString = ({ calculatedField }) => {
  const [, { touched, error }] = useField('format')

  return (
    <>
      <FieldSelector calculatedField={calculatedField} types={['datetime', 'date']} maxFields={1} />
      <FormControl id="format" isRequired isInvalid={touched && error}>
        <FormLabel>Format</FormLabel>
        <Field name="format" as={Input} maxW="3xs" />
        <FormErrorMessage>
          <ErrorMessage name="format" />
        </FormErrorMessage>
      </FormControl>
    </>
  )
}

const Round = ({ calculatedField }) => {
  const [, { touched, error }] = useField('precision')

  return (
    <>
      <FieldSelector calculatedField={calculatedField} types={['number', 'integer']} maxFields={1} />
      <FormControl id="precision" isRequired isInvalid={touched && error}>
        <FormLabel>Precision</FormLabel>
        <Field type="number" name="precision" as={Input} maxW="3xs" />
        <FormErrorMessage>
          <ErrorMessage name="precision" />
        </FormErrorMessage>
      </FormControl>
    </>
  )
}

const Avg = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['number', 'integer']} maxFields={1} />
)

const fieldSelectionValidations = (minFields = 2) => ({
  fields: Yup.array(
    Yup.object({
      path: Yup.string().required('Source is required').nullable(),
      field: Yup.string().required('Field is required').nullable()
    })
  ).min(minFields, `Must have at least ${minFields} fields`)
})

const fieldOrValueFieldSelectionValidations = (minFields = 2) => ({
  fields: Yup.array(
    Yup.object({
      path: Yup.string().required('Source is required').nullable(),
      field: Yup.string().when('path', {
        is: val => val === fieldPathKeys.value,
        then: Yup.string().nullable().notRequired(),
        otherwise: Yup.string().nullable().required('Field is required')
      }),
      value: Yup.string().when('path', {
        is: val => val !== fieldPathKeys.value,
        then: Yup.string().nullable().notRequired(),
        otherwise: Yup.string().nullable().required('Value is required')
      })
    })
  ).min(minFields, `Must have at least ${minFields} fields`)
})

const toStringValidation = () => ({
  format: Yup.string().required('Format is required').nullable()
})

const valueFieldValidation = () => ({
  value: Yup.string().required('Value is required').nullable()
})

const substringValidation = () => ({
  start_index: Yup.number()
    .nullable()
    .required('Start Index is required')
    .positive('Must be a positive number')
    .integer('Must be an integer value')
})

const lengthRequiredValidation = () => ({
  length: Yup.number()
    .nullable()
    .required('Length is required')
    .positive('Must be a positive number')
    .integer('Must be an integer value')
})

const UnNest = ({ calculatedField }) => (
  <FieldSelector calculatedField={calculatedField} types={['array']} maxFields={1} />
)

export const Formulas = [
  'Add',
  'Age',
  'ArrayToString',
  'Avg',
  'Case',
  'Coalesce',
  'Concat',
  'Count',
  'DateDiff',
  'DateTrunc',
  'Divide',
  'Min',
  'Max',
  'Multiply',
  'Round',
  'Left',
  'PadText',
  'Right',
  'Substring',
  'Subtract',
  'Sum',
  'ToString',
  'UnNest',
  'Value'
]
export const getFormulae = msg => {
  return {
    Add: {
      component: Add,
      description: 'Add up multiple numerical fields',
      type: 'number',
      validations: fieldSelectionValidations(2)
    },
    Age: {
      component: Age,
      description: 'Number of days since given date',
      type: 'number',
      validations: fieldSelectionValidations(1)
    },
    ArrayToString: {
      component: ArrayToString,
      description: 'Formats an Array into a string of comma separated values',
      type: 'string',
      validations: fieldSelectionValidations(1)
    },
    Avg: {
      component: Avg,
      description: 'Average',
      type: 'number',
      validations: fieldSelectionValidations(1)
    },
    Case: {
      component: Case,
      description: 'Transform values based on conditional statements',
      subtitle: (
        <>
          The <i>then</i> value of the first matching condition is returned
        </>
      ),
      validations: getCaseFieldValidations(msg)
    },
    Coalesce: {
      component: Coalesce,
      description: 'Returns the value of the first field that is not null',
      validations: fieldSelectionValidations(2)
    },
    Concat: {
      component: Concat,
      description: 'Concatenate multiple string type fields',
      type: 'string',
      validations: fieldOrValueFieldSelectionValidations(2)
    },
    Count: {
      component: Count,
      description: 'Count occurrences of this field value in the group of fields',
      type: 'integer',
      validations: fieldSelectionValidations(1)
    },
    DateDiff: {
      component: DateDiff,
      description: 'Date difference in days',
      type: 'number',
      validations: fieldSelectionValidations(2)
    },
    DateTrunc: {
      component: DateTrunc,
      description: 'Truncate a date field to a specified level of precision',
      type: 'date',
      validations: DateTruncValidations
    },
    Divide: {
      component: Divide,
      description: 'Divide two numerical fields',
      type: 'number',
      validations: fieldSelectionValidations(2)
    },
    Min: {
      component: Min,
      description: 'Minimum value in the group of values',
      validations: fieldSelectionValidations(1)
    },
    Max: {
      component: Max,
      description: 'Maximum value in the group of values',
      validations: fieldSelectionValidations(1)
    },
    Multiply: {
      component: Multiply,
      description: 'Multiply values of selected fields',
      validations: fieldSelectionValidations(2)
    },
    Round: {
      component: Round,
      description: 'Rounds a value to the nearest integer or a number of decimal places.',
      validations: fieldSelectionValidations(1)
    },
    Left: {
      label: 'Substring - Left',
      component: LeftOrRight,
      description: 'Extract a substring from the left side of the value.',
      validations: merge(fieldSelectionValidations(1), lengthRequiredValidation())
    },
    PadText: {
      component: PadText,
      description: 'Text Padding (Left/Right)',
      validations: PadTextValidations
    },
    Right: {
      label: 'Substring - Right',
      component: LeftOrRight,
      description: 'Extract a substring from the right side of the value.',
      validations: merge(fieldSelectionValidations(1), lengthRequiredValidation())
    },
    Substring: {
      label: 'Substring - Mid',
      component: Substring,
      description: 'Extract a substring from the start index to the length specified.',
      validations: merge(fieldSelectionValidations(1), substringValidation())
    },
    Subtract: {
      component: Subtract,
      description: 'Subtract two numerical fields',
      type: 'number',
      validations: fieldSelectionValidations(2)
    },
    Sum: {
      component: Sum,
      description: 'Aggregate function to sum values of a field in the group',
      type: 'number',
      validations: fieldSelectionValidations(1)
    },
    ToString: {
      component: ToString,
      description: 'Formats the input expression as a string',
      validations: merge(fieldSelectionValidations(1), toStringValidation())
    },
    UnNest: {
      component: UnNest,
      description: 'Un-Nest splits multiple values in a field values into multiple Rows',
      type: 'string',
      validations: fieldSelectionValidations(1),
      usageLimitations: { filterFieldDisallowed: true, formulaFieldDisallowed: true }
    },
    Value: {
      component: Value,
      description: 'Specify static value for the field',
      validations: valueFieldValidation()
    }
  }
}
